import * as React from 'react';
import cls from 'classnames';

import BreakpointsService from 'src/explore/services/breakpoints';
import {
  Expand,
  ExpandProps,
  IHeightByBreakpoint,
  useInitialHeightByBreakpoint,
} from 'src/explore/elements/Expand';
import { ExpandButton } from './elements/ExpandButton';

import styles from './index.module.sass';

export interface ExpandableContainerProps {
  className?:
    | string
    | {
        button?: string;
        container?: string;
      };
  CustomExpander?: React.FC<ExpanderProps>;
  initialHeight?: ExpandProps['initialHeight'];
  isOpen?: boolean;
  maskToLineBreak?: boolean;
  onToggleOpen?: ( open?: boolean ) => void;
}

export interface ExpanderProps {
  collapsedHeight: number | null;
  isOpen: boolean;
  overflow: boolean;
  toggleExpand: () => void;
}

type BreakPoint = keyof IHeightByBreakpoint;

export const ExpandableContainer: React.FC<ExpandableContainerProps> = ({
  children,
  className,
  CustomExpander,
  initialHeight,
  isOpen = false,
  maskToLineBreak,
  onToggleOpen,
}) => {
  const [ buttonContainerRef, setButtonContainerRef ] = React.useState<HTMLDivElement | null>( null );
  const [ maskOffset, setMaskOffset ] = React.useState<number>( 0 );
  const [ open, setOpen ] = React.useState<boolean>( isOpen );

  React.useEffect(() => setOpen( isOpen ), [ isOpen ]);

  const contentRef = React.useRef<HTMLDivElement>( null );

  const handleToggleOpen = React.useCallback(() => {
    setOpen(( prevOpen ) => {
      if ( onToggleOpen ) onToggleOpen( !prevOpen );

      return !prevOpen;
    });
  }, [ onToggleOpen ]);

  const buttonContainerHeight = buttonContainerRef ? buttonContainerRef.offsetHeight : 0;

  // add button height to the initial height of the container
  const heightWithButton = React.useMemo(() => {
    switch ( typeof initialHeight ) {
      case 'number':
        return initialHeight + buttonContainerHeight;

      case 'object':
        return Object.keys( initialHeight ).reduce(
          ( prev, curr ) => ({
            ...prev,
            [curr]:
              initialHeight[curr as BreakPoint] !== null
                ? initialHeight[curr as BreakPoint] + buttonContainerHeight
                : null,
          }),
          {} as IHeightByBreakpoint
        );

      default:
        return undefined;
    }
  }, [ initialHeight, buttonContainerHeight ]);

  const currentCollapseHeight =
    useInitialHeightByBreakpoint( initialHeight )[
      BreakpointsService.getBreakpointSize() as keyof IHeightByBreakpoint
    ];

  React.useEffect(() => {
    const findLastLineBreak = () => {
      if ( maskToLineBreak && contentRef.current ) {
        let child: Element;
        let style: CSSStyleDeclaration;
        let lineHeight: number;
        for ( let i = 0; i < contentRef.current.children.length; i += 1 ) {
          child = contentRef.current.children[i];

          // find element under edge of mask
          if (
            child instanceof HTMLElement &&
            child.offsetTop + child.offsetHeight >= currentCollapseHeight
          ) {
            style = window.getComputedStyle( child );
            lineHeight = parseInt( style.lineHeight, 10 );

            // offset mask to set edge on line break
            setMaskOffset(( currentCollapseHeight - child.offsetTop ) % lineHeight );
            break;
          }
        }
      }
    };
    findLastLineBreak();
    window.addEventListener( 'resize', findLastLineBreak );
    return () => {
      window.removeEventListener( 'resize', findLastLineBreak );
    };
  }, [ contentRef, currentCollapseHeight, initialHeight, maskToLineBreak ]);

  // legacy classname support
  const classNames = typeof className === 'object' ? className : { container: className };

  return (
    <Expand
      className={cls( 'position-relative', classNames.container )}
      isOpen={open}
      initialHeight={heightWithButton}
    >
      {({ collapsedHeight, overflow }) => (
        <>
          <div ref={contentRef}>{children}</div>

          <div
            className={cls({
              'position-absolute w-100 bg-white': overflow,
              [styles.mask]: overflow,
              [styles.hide]: overflow && open,
            })}
            style={{ top: collapsedHeight - buttonContainerHeight - maskOffset }}
          />

          <div
            ref={( newRef ) => setButtonContainerRef( newRef )}
            className={cls( styles.buttonContainer, 'position-absolute w-100', {
              [styles.open]: open || !overflow,
            })}
            style={{ top: collapsedHeight - buttonContainerHeight }}
          >
            {CustomExpander ? (
              <CustomExpander
                collapsedHeight={collapsedHeight}
                isOpen={open}
                overflow={overflow}
                toggleExpand={handleToggleOpen}
              />
            ) : (
              <ExpandButton className={classNames.button} onClick={handleToggleOpen} />
            )}
          </div>
        </>
      )}
    </Expand>
  );
};
